// src/components/landing/AppBlock.tsx
// Bloco — "Seu estudo vai com você" | App em breve
// Prefixo CSS: appb-

import React, { useState, useEffect, useRef } from 'react';
import { Wifi, WifiOff, RefreshCcw, BarChart2, Clock, Zap } from 'lucide-react';
import './AppBlock.css';

// ─── Telas do mockup interativo ───────────────────────────────────────────────
const SCREENS = [
    {
        id: 'flashcard',
        label: 'Flashcards',
        content: (
            <div className="appb-screen-body appb-screen-body--card">
                <div className="appb-mock-header">
                    <span className="appb-mock-logo">GRAIA</span>
                    <span className="appb-mock-sep" />
                    <span className="appb-mock-mode">Revisão Adaptativa</span>
                    <span className="appb-mock-counter">7 / 18</span>
                </div>
                <div className="appb-mock-mastery">
                    <div className="appb-mock-mastery-row">
                        <span>Domínio</span><strong>72%</strong>
                    </div>
                    <div className="appb-mock-mastery-track">
                        <div className="appb-mock-mastery-fill appb-mock-mastery-fill--72" />
                    </div>
                </div>
                <div className="appb-mock-card-content">
                    <span className="appb-mock-tag">Cardiologia</span>
                    <p className="appb-mock-question">
                        Qual artéria está mais associada ao supra de ST em DII, DIII e aVF?
                    </p>
                    <div className="appb-mock-answer">
                        <span className="appb-mock-answer-label">Resposta</span>
                        <p className="appb-mock-answer-text">
                            Coronária direita.
                        </p>
                    </div>
                </div>
                <div className="appb-mock-ratings">
                    {[1, 2, 3, 4, 5].map(n => (
                        <button key={n} className={`appb-mock-rating appb-mock-rating--${n}`}>
                            <span>{n}</span>
                        </button>
                    ))}
                </div>
            </div>
        ),
    },
    {
        id: 'agenda',
        label: 'Revisões',
        content: (
            <div className="appb-screen-body appb-screen-body--agenda">
                <div className="appb-mock-header">
                    <span className="appb-mock-logo">GRAIA</span>
                    <span className="appb-mock-sep" />
                    <span className="appb-mock-mode">Agenda de Revisões</span>
                </div>
                <div className="appb-agenda-today">
                    <span className="appb-agenda-label">Hoje</span>
                    <span className="appb-agenda-count">8 cards pendentes</span>
                </div>
                {[
                    { deck: 'Cardiologia', sub: 'IC e Arritmias', n: 4, urgency: 'high' },
                    { deck: 'Neurologia', sub: 'Epilepsia', n: 3, urgency: 'med' },
                    { deck: 'Pneumologia', sub: 'DPOC', n: 1, urgency: 'low' },
                ].map(item => (
                    <div key={item.deck} className={`appb-agenda-item appb-agenda-item--${item.urgency}`}>
                        <div className="appb-agenda-dot" />
                        <div className="appb-agenda-info">
                            <span className="appb-agenda-deck">{item.deck}</span>
                            <span className="appb-agenda-subdeck">{item.sub}</span>
                        </div>
                        <span className="appb-agenda-n">{item.n}</span>
                    </div>
                ))}
                <div className="appb-agenda-tomorrow">
                    <span className="appb-agenda-label">Amanhã</span>
                    <span className="appb-agenda-count">5 cards</span>
                </div>
            </div>
        ),
    },
    {
        id: 'metrics',
        label: 'Métricas',
        content: (
            <div className="appb-screen-body appb-screen-body--metrics">
                <div className="appb-mock-header">
                    <span className="appb-mock-logo">GRAIA</span>
                    <span className="appb-mock-sep" />
                    <span className="appb-mock-mode">Desempenho</span>
                </div>
                <div className="appb-metrics-row">
                    <div className="appb-metric-card">
                        <span className="appb-metric-val">347</span>
                        <span className="appb-metric-label">Cards dominados</span>
                    </div>
                    <div className="appb-metric-card">
                        <span className="appb-metric-val appb-metric-val--green">81%</span>
                        <span className="appb-metric-label">Domínio geral</span>
                    </div>
                </div>
                <div className="appb-metrics-streak">
                    <Zap size={13} />
                    <span><strong>12 dias</strong> de sequência ativa</span>
                </div>
                <div className="appb-metrics-bars">
                    {[
                        { label: 'Seg', h: 70 }, { label: 'Ter', h: 85 },
                        { label: 'Qua', h: 55 }, { label: 'Qui', h: 90 },
                        { label: 'Sex', h: 75 }, { label: 'Sáb', h: 40 },
                        { label: 'Dom', h: 60 },
                    ].map(bar => (
                        <div key={bar.label} className="appb-bar-col">
                            <div className="appb-bar-track">
                                <div className="appb-bar-fill" style={{ height: `${bar.h}%` }} />
                            </div>
                            <span className="appb-bar-label">{bar.label}</span>
                        </div>
                    ))}
                </div>
            </div>
        ),
    },
];

// ─── Componente principal ──────────────────────────────────────────────────────
const AppBlock: React.FC = () => {
    const sectionRef = useRef<HTMLElement>(null);
    const [visible, setVisible] = useState(false);
    const [activeScreen, setActiveScreen] = useState(0);
    const [isOnline, setIsOnline] = useState(true);
    const [syncPulse, setSyncPulse] = useState(false);

    // IntersectionObserver — mesmo padrão de todos os blocos GRAIA
    useEffect(() => {
        const obs = new IntersectionObserver(
            ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
            { threshold: 0.1 }
        );
        if (sectionRef.current) obs.observe(sectionRef.current);
        return () => obs.disconnect();
    }, []);

    // Auto-rotate das telas do mockup
    useEffect(() => {
        if (!visible) return;
        const t = setInterval(() => {
            setActiveScreen(prev => (prev + 1) % SCREENS.length);
        }, 4200);
        return () => clearInterval(t);
    }, [visible]);

    // Toggle offline demo
    const handleOfflineDemo = () => {
        setIsOnline(false);
        setTimeout(() => setIsOnline(true), 2400);
    };

    // Pulso de sync ao mudar tela
    useEffect(() => {
        setSyncPulse(true);
        const t = setTimeout(() => setSyncPulse(false), 600);
        return () => clearTimeout(t);
    }, [activeScreen]);

    const benefits = [
        { icon: WifiOff, text: 'Estude offline, sem depender de conexão constante' },
        { icon: BarChart2, text: 'Sincronização automática com todo o seu histórico' },
        { icon: Clock, text: 'Interface otimizada' },
    ];

    return (
        <section
            ref={sectionRef}
            className={`appb-section ${visible ? 'appb-section--visible' : ''}`}
        >
            {/* Fundo: grade sutil + glows */}
            <div className="appb-bg" aria-hidden="true">
                <div className="appb-bg-grid" />
                <div className="appb-bg-glow appb-bg-glow--1" />
                <div className="appb-bg-glow appb-bg-glow--2" />
            </div>

            <div className="appb-container">

                {/* ── SPLIT: esquerda copy / direita mockup ── */}
                <div className="appb-split">

                    {/* ── COPY ── */}
                    <div className="appb-copy">

                        <div className="appb-eyebrow">
                            <span className="appb-eyebrow-dot" />
                            Em desenvolvimento ativo
                        </div>

                        <h2 className="appb-headline">
                            Continue de onde parou<br />
                            <em className="appb-headline-em">em qualquer lugar.</em>
                        </h2>

                        <p className="appb-subheadline">
                            O aplicativo GRAIA leva sua rotina de estudo — revisões, questões e métricas — para qualquer momento do seu dia, com continuidade total entre dispositivos.
                        </p>

                        <ul className="appb-benefits">
                            {benefits.map((b, i) => {
                                const Icon = b.icon;
                                return (
                                    <li
                                        key={i}
                                        className="appb-benefit-item"
                                        style={{ animationDelay: visible ? `${0.55 + i * 0.12}s` : '0s' }}
                                    >
                                        <span className="appb-benefit-icon">
                                            <Icon size={13} strokeWidth={2} />
                                        </span>
                                        <span className="appb-benefit-text">{b.text}</span>
                                    </li>
                                );
                            })}
                        </ul>

                        <div className="appb-early-access">
                            <div className="appb-early-badge">
                                <span className="appb-early-pulse" />
                                Usuários ativos terão acesso antecipado ao aplicativo.
                            </div>
                        </div>

                    </div>

                    {/* ── MOCKUP ── */}
                    <div className="appb-mockup-area">

                        {/* Telefone principal */}
                        <div className="appb-phone">
                            <div className="appb-phone-island" />

                            <div className="appb-phone-screen">
                                {/* Selector de tela */}
                                <div className="appb-screen-tabs">
                                    {SCREENS.map((s, i) => (
                                        <button
                                            key={s.id}
                                            className={`appb-screen-tab ${activeScreen === i ? 'appb-screen-tab--active' : ''}`}
                                            onClick={() => setActiveScreen(i)}
                                        >
                                            {s.label}
                                        </button>
                                    ))}
                                </div>

                                {/* Conteúdo da tela atual */}
                                <div className="appb-screen-content" key={activeScreen}>
                                    {SCREENS[activeScreen].content}
                                </div>

                                {/* Barra de status inferior */}
                                <div className="appb-status-bar">
                                    <button
                                        className={`appb-offline-toggle ${!isOnline ? 'appb-offline-toggle--offline' : ''}`}
                                        onClick={handleOfflineDemo}
                                        title="Testar modo offline"
                                    >
                                        {isOnline
                                            ? <><Wifi size={10} /><span>Online</span></>
                                            : <><WifiOff size={10} /><span>Offline — salvando localmente</span></>
                                        }
                                    </button>
                                    <div className={`appb-sync-indicator ${syncPulse ? 'appb-sync-indicator--pulse' : ''}`}>
                                        <RefreshCcw size={9} />
                                        <span>Sincronizado</span>
                                    </div>
                                </div>
                            </div>

                            <div className="appb-phone-bar" />
                        </div>



                        {/* Badge de plataforma */}
                        <div className="appb-platform-badge">
                            iOS &amp; Android
                        </div>

                    </div>
                </div>

            </div>
        </section>
    );
};

export default AppBlock;